// Pure, UI-agnostic filtering for the camera store-tree (device → cameras → views).
//
// Semantics (kept deliberately simple and predictable):
//   * A node is KEPT if it directly matches the active filters, OR if any of its
//     descendants is kept (so ancestors stay visible for context).
//   * A directly-matched node keeps its ENTIRE subtree, so its children stay visible
//     (e.g. matching a camera surfaces all of its views). A node kept only because a
//     descendant matched is pruned to the matching path.
//   * Filters only let the kinds they apply to match directly; a kind a filter does
//     not apply to can still appear as an ancestor of a deeper match:
//       - status     → cameras and views
//       - machine    → cameras only
//       - segment    → cameras only
//       - has-polygon → views only
//
// The function is independent of any async UI state (e.g. the inference-machine
// label cache): it filters on the raw ids carried by each node. The one piece of
// reference data it cannot derive from the tree — the segment of a camera's alert
// channel — is supplied explicitly via the `ctx.segmentByChannelId` map, keeping
// the function pure (same inputs → same output).

import { diffInternals } from "../shared/diff";

// alert_channel may be a plain id or a nested { id } object; reuse the shared
// normalizer rather than duplicating the shape handling.
const { fkValue } = diffInternals;

export const EMPTY_FILTERS = Object.freeze({
  query: "",
  status: "",
  machineId: null,
  segment: "",
  hasPolygon: false,
});

function machineIsSet(machineId) {
  return machineId != null && machineId !== "";
}

/**
 * @param {object} filters - { query, status, machineId, segment, hasPolygon }
 * @returns {boolean} true if at least one filter constrains the result.
 */
export function isFilterActive(filters) {
  if (!filters) return false;
  const { query, status, machineId, segment, hasPolygon } = filters;
  return Boolean((query && query.trim()) || status || machineIsSet(machineId) || segment || hasPolygon);
}

/**
 * Distinct, non-null segment keys present among the cameras' alert channels.
 * @param {Array} tree
 * @param {Object} segmentByChannelId - map of alert-channel id → segment key
 * @returns {Array<string>}
 */
export function collectSegmentOptions(tree, segmentByChannelId) {
  const map = segmentByChannelId || {};
  const set = new Set();
  (tree || []).forEach((device) =>
    (device.cameras || []).forEach((camera) => {
      const seg = map[fkValue(camera.alert_channel)];
      if (seg) set.add(seg);
    })
  );
  return [...set];
}

/**
 * Unique, non-null inference-machine ids present in the tree (for the filter dropdown).
 * @param {Array} tree
 * @returns {Array<number>}
 */
export function collectMachineOptions(tree) {
  const set = new Set();
  (tree || []).forEach((device) =>
    (device.cameras || []).forEach((camera) => {
      if (camera.inference_machine != null) set.add(camera.inference_machine);
    })
  );
  return [...set];
}

const deviceText = (d) => `${d.public_id || ""} ${d.name || ""}`.toLowerCase();
const cameraText = (c) => `${c.public_id || ""} camera ${c.slot_number ?? ""}`.toLowerCase();
const viewText = (v) => `${v.public_id || ""} view ${v.view_number ?? ""} ${v.view_name || ""}`.toLowerCase();

const textMatches = (text, query) => !query || text.includes(query);

// `is_active` is treated as active unless explicitly false (matches buildTreeNodes).
const statusMatches = (isActive, status) => !status || (isActive !== false) === (status === "active");

const hasPolygonArea = (view) => Array.isArray(view.view_area) && view.view_area.length > 0;

// Camera-level HARD gates: a camera failing these is excluded together with its whole
// subtree (it cannot even appear as an ancestor). They are path constraints, so they
// combine correctly with a view-level filter — e.g. machine + has-polygon keeps the
// polygon views *under* cameras on that machine, rather than cancelling each other out.
const machineGateOk = (camera, filters) =>
  !machineIsSet(filters.machineId) || camera.inference_machine === filters.machineId;
const segmentGateOk = (camera, filters, ctx) =>
  !filters.segment || (ctx.segmentByChannelId || {})[fkValue(camera.alert_channel)] === filters.segment;

// A device directly matches only when text search is the sole active filter; every
// other filter targets a deeper level, so a device can otherwise only be an ancestor.
const deviceDirectlyMatches = (device, filters, query) =>
  !filters.status &&
  !machineIsSet(filters.machineId) &&
  !filters.segment &&
  !filters.hasPolygon &&
  textMatches(deviceText(device), query);

// A view matches on the view-level criteria (has-polygon + status + text).
const viewMatches = (view, filters, query) =>
  (!filters.hasPolygon || hasPolygonArea(view)) &&
  statusMatches(view.is_active, filters.status) &&
  textMatches(viewText(view), query);

// A directly-matched node is shown with its full subtree; expand every level that
// has children so they are actually visible.
function markSubtreeExpandable(device, expandIds) {
  const cameras = device.cameras || [];
  if (cameras.length === 0) return;
  expandIds.add(device.public_id);
  cameras.forEach((camera) => {
    if ((camera.views || []).length > 0) expandIds.add(camera.public_id);
  });
}

/**
 * Filter the store-tree.
 * @param {Array} tree - devices, each with `cameras`, each with `views`.
 * @param {object} filters - { query, status, machineId, segment, hasPolygon } (see EMPTY_FILTERS).
 * @param {object} [ctx] - reference data not derivable from the tree.
 * @param {Object} [ctx.segmentByChannelId] - map of alert-channel id → segment key.
 * @returns {{ tree: Array, expandIds: Set<string>, matchCount: number }}
 *          `tree` is a pruned copy, `expandIds` are the public_ids of kept nodes
 *          whose children should be revealed, `matchCount` counts matched nodes.
 */
export function filterTree(tree, filters, ctx = {}) {
  const f = filters || EMPTY_FILTERS;
  if (!isFilterActive(f)) {
    return { tree: tree || [], expandIds: new Set(), matchCount: 0 };
  }

  const query = (f.query || "").trim().toLowerCase();
  const expandIds = new Set();
  let matchCount = 0;
  const keptDevices = [];

  (tree || []).forEach((device) => {
    if (deviceDirectlyMatches(device, f, query)) {
      // Direct match → keep the whole subtree so its children stay visible.
      matchCount += 1;
      keptDevices.push(device);
      markSubtreeExpandable(device, expandIds);
      return;
    }

    const keptCameras = [];
    (device.cameras || []).forEach((camera) => {
      // Hard camera gates first: machine + segment exclude the camera and its subtree.
      if (!machineGateOk(camera, f) || !segmentGateOk(camera, f, ctx)) return;

      // The camera itself is a match when no view-level filter (has-polygon) is active
      // and it clears its own status + text criteria → keep its full subtree.
      const cameraIsMatch =
        !f.hasPolygon && statusMatches(camera.is_active, f.status) && textMatches(cameraText(camera), query);
      if (cameraIsMatch) {
        matchCount += 1;
        keptCameras.push(camera);
        if ((camera.views || []).length > 0) expandIds.add(camera.public_id);
        return;
      }

      // Otherwise keep the matching views (the gate-passing camera is their ancestor).
      const keptViews = (camera.views || []).filter((view) => viewMatches(view, f, query));
      if (keptViews.length > 0) {
        matchCount += keptViews.length;
        keptCameras.push({ ...camera, views: keptViews });
        expandIds.add(camera.public_id);
      }
    });

    if (keptCameras.length > 0) {
      keptDevices.push({ ...device, cameras: keptCameras });
      expandIds.add(device.public_id);
    }
  });

  return { tree: keptDevices, expandIds, matchCount };
}
